import { createSelector } from '@ngrx/store';

import { AppState } from '../root-reducer';
import { OwnRestrictedListActions, OwnRestrictedListActionTypes } from 'store/actions/own-restricted-list.actions';
import { OwnRestrictedList, OwnRestrictedName } from 'modules/shared/models/own-restricted-list.models';
import { OwnRestrictedListsResponse, OwnRestrictedNamesResponse } from 'store/models/own-restricted-list.models';

export interface State {
  loading: Array<OwnRestrictedListActionTypes>;
  ownRestrictedList:  OwnRestrictedList | null;
  ownRestrictedLists: OwnRestrictedListsResponse | null;
  addRestricted: OwnRestrictedList | null;
  editRestricted: OwnRestrictedList | null;
  restrictedNames:  OwnRestrictedNamesResponse | null;
  addRestrictedName: OwnRestrictedName | null;
  editRestrictedName: OwnRestrictedName| null;
  err: string | null;
}

const initialState = {
  loading: [],
  ownRestrictedList: null,
  ownRestrictedLists: null,
  addRestricted: null,
  editRestricted: null,
  restrictedNames:  null,
  addRestrictedName: null,
  editRestrictedName: null,
  err: null,
};

export function reducer(state: State = initialState, action: OwnRestrictedListActions): State {
  switch (action.type) {
    case OwnRestrictedListActionTypes.GET_OWN_RESTRICTED_LIST: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case OwnRestrictedListActionTypes.GET_OWN_RESTRICTED_LIST_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, OwnRestrictedListActionTypes.GET_OWN_RESTRICTED_LIST),
        ownRestrictedList: action.payload
      };
    }
    case OwnRestrictedListActionTypes.GET_OWN_RESTRICTED_LIST_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, OwnRestrictedListActionTypes.GET_OWN_RESTRICTED_LIST),
        err: action.payload
      };
    }
    case OwnRestrictedListActionTypes.GET_OWN_RESTRICTED_LISTS: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case OwnRestrictedListActionTypes.GET_OWN_RESTRICTED_LISTS_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, OwnRestrictedListActionTypes.GET_OWN_RESTRICTED_LISTS),
        ownRestrictedLists: action.payload
      };
    }
    case OwnRestrictedListActionTypes.GET_OWN_RESTRICTED_LISTS_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, OwnRestrictedListActionTypes.GET_OWN_RESTRICTED_LISTS),
        err: action.payload
      };
    }
    case OwnRestrictedListActionTypes.CREATE_OWN_RESTRICTED_LIST: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case OwnRestrictedListActionTypes.CREATE_OWN_RESTRICTED_LIST_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, OwnRestrictedListActionTypes.CREATE_OWN_RESTRICTED_LIST),
        addRestricted: action.payload
      };
    }
    case OwnRestrictedListActionTypes.CREATE_OWN_RESTRICTED_LIST_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, OwnRestrictedListActionTypes.CREATE_OWN_RESTRICTED_LIST),
        err: action.payload
      };
    }
    case OwnRestrictedListActionTypes.UPDATE_OWN_RESTRICTED_LIST: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case OwnRestrictedListActionTypes.UPDATE_OWN_RESTRICTED_LIST_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, OwnRestrictedListActionTypes.UPDATE_OWN_RESTRICTED_LIST),
        editRestricted: action.payload
      };
    }
    case OwnRestrictedListActionTypes.UPDATE_OWN_RESTRICTED_LIST_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, OwnRestrictedListActionTypes.UPDATE_OWN_RESTRICTED_LIST),
        err: action.payload
      };
    }
    case OwnRestrictedListActionTypes.GET_RESTRICTED_NAMES: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case OwnRestrictedListActionTypes.GET_RESTRICTED_NAMES_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, OwnRestrictedListActionTypes.GET_RESTRICTED_NAMES),
        restrictedNames: action.payload
      };
    }
    case OwnRestrictedListActionTypes.GET_RESTRICTED_NAMES_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, OwnRestrictedListActionTypes.GET_RESTRICTED_NAMES),
        err: action.payload
      };
    }
    case OwnRestrictedListActionTypes.CREATE_RESTRICTED_NAME: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case OwnRestrictedListActionTypes.CREATE_RESTRICTED_NAME_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, OwnRestrictedListActionTypes.CREATE_RESTRICTED_NAME),
        addRestrictedName: action.payload
      };
    }
    case OwnRestrictedListActionTypes.CREATE_RESTRICTED_NAME_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, OwnRestrictedListActionTypes.CREATE_RESTRICTED_NAME),
        err: action.payload
      };
    }
    case OwnRestrictedListActionTypes.EDIT_RESTRICTED_NAME: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case OwnRestrictedListActionTypes.EDIT_RESTRICTED_NAME_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, OwnRestrictedListActionTypes.EDIT_RESTRICTED_NAME),
        editRestrictedName: action.payload
      };
    }
    case OwnRestrictedListActionTypes.EDIT_RESTRICTED_NAME_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, OwnRestrictedListActionTypes.EDIT_RESTRICTED_NAME),
        err: action.payload
      };
    }
    case OwnRestrictedListActionTypes.RESET_RESTRICTED_STATE: {
      return initialState;
    }

    default: {
      return state;
    }
  }
}

const ownRestrictedList = (state: AppState) => state.ownRestrictedList;
export const Selectors = {
  getErrors: createSelector(ownRestrictedList, (state: State) => state.err),
  getLoading: createSelector(ownRestrictedList, (state: State) => state.loading),
  getOwnRestrictedList: createSelector(ownRestrictedList, (state: State) => state.ownRestrictedList),
  getOwnRestrictedLists: createSelector(ownRestrictedList, (state: State) => state.ownRestrictedLists),
  addRestricted: createSelector(ownRestrictedList, (state: State) => state.addRestricted),
  editRestricted: createSelector(ownRestrictedList, (state: State) => state.editRestricted),
  getRestrictedNames: createSelector(ownRestrictedList, (state: State) => state.restrictedNames),
  addRestrictedName: createSelector(ownRestrictedList, (state: State) => state.addRestrictedName),
  editRestrictedName: createSelector(ownRestrictedList, (state: State) => state.editRestrictedName)
};

function clearLoading(authState: State, action: OwnRestrictedListActionTypes): Array<OwnRestrictedListActionTypes> {
  return authState.loading.filter((loadingMarker: OwnRestrictedListActionTypes) => loadingMarker !== action);
}
